import { useCallback, useReducer, useEffect, useMemo } from 'react';
import { useDispatch as useReduxDispatch } from 'react-redux';
import { setError } from '@/actions';
import { useTranslation } from 'react-i18next';
import { generateNITCode, validateNITCode } from '../../../actions/payment';
import useBackoff from '../../../hooks/useBackoff';
import useQueue from '../../../hooks/useQueue';

// Action types
const ACTIONS = {
  START: 'START',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  RESET: 'RESET',
};

// Initial state
const initialState = {
  operation: null, // 'create' or 'confirm'
  isLoading: false,
  codeSent: false,
  error: null,
  errorMessage: null,
};

// Reducer function
const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.START:
      return {
        ...state,
        operation: action.payload.operation,
        isLoading: true,
        error: null,
        errorMessage: null,
      };
    case ACTIONS.SUCCESS:
      return {
        ...state,
        isLoading: false,
        codeSent: action.payload.operation === 'create' ? true : state.codeSent,
        operation: null,
      };
    case ACTIONS.ERROR:
      return {
        ...state,
        isLoading: false,
        error: action.payload.errorType,
        errorMessage: action.payload.message,
        operation: null,
      };
    case ACTIONS.RESET:
      return {
        ...initialState,
      };
    default:
      return state;
  }
};

/**
 * Hook to handle the redeem confirmation
 * @param {object} props - Hook properties
 * @param {Function} props.onSuccessConfirmation - The success callback
 * @returns {object} redeemConfirmation - The redeem confirmation
 */
const useRedeemConfirmation = ({ onSuccessConfirmation }) => {
  const dispatchRedux = useReduxDispatch();
  const { t } = useTranslation(['error']);
  const [state, dispatch] = useReducer(reducer, initialState);

  // Use the backoff hook
  const backoff = useBackoff();

  // Function to execute when processing a queue item
  const executeCreateNITCode = useCallback(
    async (notificationType) => {
      try {
        await dispatchRedux(generateNITCode({ notificationType }));
        dispatch({ type: ACTIONS.SUCCESS, payload: { operation: 'create' } });
        backoff.resetTimer();
      } catch (error) {
        // The generateNITCode function now throws specific error messages based on HTTP status codes
        const errorMessage = error?.message || t('error.something_went_wrong');

        console.error('Error generating NIT code', errorMessage);

        dispatch({
          type: ACTIONS.ERROR,
          payload: {
            errorType: 'generateNITCode',
            message: errorMessage,
          },
        });
        backoff.startTimer();
      }
    },
    [dispatchRedux, backoff, t],
  );

  // Use the queue hook with the execute function
  const requestQueue = useQueue(executeCreateNITCode);

  // Process queue when backoff timer becomes inactive
  useEffect(() => {
    if (!backoff.isActive && !requestQueue.isEmpty) {
      requestQueue.processQueue();
    }
  }, [backoff.isActive, requestQueue]);

  const createNITCode = useCallback(
    async (notificationType) => {
      if (!requestQueue.isEmpty) return;

      dispatch({ type: ACTIONS.START, payload: { operation: 'create' } });

      // If backoff timer is active, add to queue and return
      if (backoff.isActive) {
        requestQueue.addToQueue(notificationType);
        return;
      }

      // Execute immediately if not in backoff
      await executeCreateNITCode(notificationType);
    },
    [backoff.isActive, executeCreateNITCode, requestQueue],
  );

  const confirmNITCode = useCallback(
    async (code) => {
      dispatch({ type: ACTIONS.START, payload: { operation: 'confirm' } });

      try {
        const response = await dispatchRedux(validateNITCode(code));

        dispatchRedux(setError(null, 'redeem_success', 'success', false));
        dispatch({ type: ACTIONS.SUCCESS, payload: { operation: 'confirm' } });
        onSuccessConfirmation?.();

        return response;
      } catch (error) {
        // The validateNITCode function now throws specific error messages based on HTTP status codes
        const errorMessage = error?.message || t('error.something_went_wrong');

        console.error('Error confirming NIT code', errorMessage);

        dispatch({
          type: ACTIONS.ERROR,
          payload: {
            errorType: 'validateNITCode',
            message: errorMessage,
          },
        });
      }
    },
    [dispatchRedux, onSuccessConfirmation, t],
  );

  const reset = useCallback(() => {
    dispatch({ type: ACTIONS.RESET });
    backoff.resetTimer();
    requestQueue.clearQueue();
  }, [backoff, requestQueue]);

  return useMemo(() => {
    return {
      isCreating: state.operation === 'create' && state.isLoading,
      isConfirming: state.operation === 'confirm' && state.isLoading,
      codeSent: state.codeSent,
      error: state.error,
      errorMessage: state.errorMessage,
      timerActive: backoff.isActive,
      nextRetryDelay: backoff.delay,
      retryCount: backoff.retryCount,
      requestQueue: requestQueue.queue,
      createNITCode,
      confirmNITCode,
      reset,
    };
  }, [state, backoff, requestQueue, createNITCode, confirmNITCode, reset]);
};

export default useRedeemConfirmation;
